import React, { useEffect, useState } from "react";
import Input from "../Input";
import Button from "../Button";
import HtmlEditor from "../HtmlEditor";
import Selector from "../Selector";
import useSWRMutation from "swr/mutation";
import { CategoryApiUrls, StaticPageApiUrls } from "../../../Api/Auth/ApiUrls";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../Api/swrConfig";
import { toast } from "react-toastify";
import useToastError from "../../../hooks/useErrorToast";
import useSWR from "swr";
import Modal from "../Modal";
import Storage from "../../../Pages/Admin/Storage";
import ImageSelector from "../ImageSelector";
import ToggleSwitch from "../ToggleSwitch";
import { useAuth } from "../../../Context/AuthContext";

const StaticPageForm = ({
  title,
  customSlug,
  typeData,
  type = "tests",
  category,
  filter,
  subscription,
  promo,
  multipleImage,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const { categoryMutate } = useAuth();
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[\s]+/g, "-") // Replace spaces with hyphens
      .replace(/[^\w-]+/g, ""); // Remove special characters
  };
  const { showErrorToast } = useToastError();
  const slug = generateSlug(title || "");
  const isMultipleImagesEnabled = Boolean(multipleImage);

  const parseImageValue = (value) => {
    if (!isMultipleImagesEnabled) {
      return typeof value === "string" ? value : value?.[0] || "";
    }
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.filter(Boolean);
    }
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.filter(Boolean);
        }
      } catch (error) {
        return value ? [value] : [];
      }
      return value ? [value] : [];
    }
    return [];
  };

  const formatImageValueForSubmit = (value) => {
    if (!isMultipleImagesEnabled) {
      if (Array.isArray(value)) {
        return value[0] || "";
      }
      return value || "";
    }
    const files = Array.isArray(value) ? value.filter(Boolean) : [];
    return JSON.stringify(files);
  };

  const [formData, setFormData] = useState({
    title: title || "",
    displayTitle: "",
    slug: customSlug || slug,
    category: null,
    headerDescription: "",
    bodyDescription: "",
    visibility: true,
    image: isMultipleImagesEnabled ? [] : "",
    [type]: [],
    promoCode: null,
  });
  const [errors, setErrors] = useState({});
  const { data } = useSWR(slug ? StaticPageApiUrls.info(slug) : null);
  const { data: categoryData } = useSWR(CategoryApiUrls.list);
  const [promoCodeOptions, setPromoCodeOptions] = useState([]);

  useEffect(() => {
    if (data?.data) {
      if (
        data?.data?.promoCodeOptions &&
        Array.isArray(JSON.parse(data?.data?.promoCodeOptions))
      ) {
        setPromoCodeOptions(JSON.parse(data?.data?.promoCodeOptions));
      }

      // If there's an existing promo code and it's not in options, add it
      if (data?.data?.promoCode) {
        const parsedPromoCodes = JSON.parse(data.data.promoCode); // Expecting an array

        if (Array.isArray(parsedPromoCodes)) {
          setPromoCodeOptions((prev) => {
            const newOptions = parsedPromoCodes.filter(
              (code) => !prev.some((opt) => opt.value === code.value)
            );
            return [...prev, ...newOptions];
          });
        }
      }
      setFormData({
        title: data?.data.title,
        displayTitle: data?.data?.displayTitle,
        slug: data?.data.slug,
        category: data?.data?.category?._id,
        headerDescription: data?.data.headerDescription,
        visibility: data?.data.visibility,
        bodyDescription: data?.data.bodyDescription,
        image: parseImageValue(data?.data.image),
        [type]: data?.data?.[type].map((item) => ({
          label: item.title,
          value: item._id,
        })),
        promoCode: data?.data?.promoCode
          ? JSON.parse(data?.data?.promoCode)
          : null,
        ...(subscription &&
          data?.data?.content && {
            subscription_duration: JSON.parse(data?.data?.content)
              ?.subscription_duration,
            subscription_price: JSON.parse(data?.data?.content)
              ?.subscription_price,
          }),
      });
    }
  }, [data, type, subscription]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.category && !category)
      newErrors.category = "Category is required";
    if (!formData.slug) newErrors.slug = "Slug is required.";
    if (!formData.headerDescription)
      newErrors.headerDescription = "Header description is required.";
    if (subscription && !formData.subscription_duration)
      newErrors.subscription_duration = "Subscription Duration is required.";
    if (subscription && !formData.subscription_price)
      newErrors.subscription_price = "Subscription price is required.";
    // Add more validation logic as needed
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    if (name === "promoCode" && Array.isArray(value)) {
      const invalid = value.find(
        (item) => !/^[a-zA-Z]+-\d+$/.test(item.label.trim())
      );

      if (invalid) {
        setErrors({
          promoCode: "Invalid promo code format. Must be in format like abc-30",
        });
        return; // Exit the whole function early
      } else {
        setErrors({});
      }
    }

    setFormData((prevState) => {
      const updatedData = { ...prevState, [name]: value };

      if (name === "title") {
        updatedData.slug = generateSlug(value); // Use `value` instead of `formData.title`
      }
      // Handle creatable promo code option
      if (name === "promoCode" && Array.isArray(value)) {
        const allSelected = [];
        value.forEach((item) => {
          const label = item.label.trim();
          const newOption = {
            label,
            value: `new-${generateSlug(label)}`,
          };
          setPromoCodeOptions((prev) => {
            const exists = prev.some((opt) => opt.label === newOption.label);
            return exists ? prev : [...prev, newOption];
          });
          allSelected.push(newOption);
        });

        updatedData.promoCode = allSelected;
      }

      if (name === "image") {
        if (isMultipleImagesEnabled) {
          updatedData.image = Array.isArray(value)
            ? value.filter(Boolean)
            : value
            ? [value]
            : [];
        } else {
          updatedData.image = Array.isArray(value)
            ? value[0] || ""
            : value || "";
        }
        return updatedData;
      }

      return updatedData;
    });
  };

  const { trigger, isMutating } = useSWRMutation(
    StaticPageApiUrls.edit(formData.slug),
    sendPutRequestAuth,
    {
      onSuccess: () => {
        toast.success("Success");
        categoryMutate();
      },
      onError: (error) => {
        if (error.response.data.message === "Static Page not found.") {
          toast.warn("Static Page not found. Creating a new one.");
          if (!validateForm()) return;
          const subscriptionContent = subscription
            ? JSON.stringify({
                subscription_price: formData.subscription_price,
                subscription_duration: formData.subscription_duration,
              })
            : undefined;
          const modifiedData = {
            ...formData,
            [type]: formData?.[type].map((item) => item.value),
            promoCode: formData?.promoCode
              ? JSON.stringify(formData?.promoCode)
              : null,
            promoCodeOptions: JSON.stringify(promoCodeOptions), // Send all promo code options
            category:
              category === "civil"
                ? categoryData?.data?.find((item) => item.title === "Civil")
                    ?._id
                : category === "mechanical"
                ? categoryData?.data?.find(
                    (item) => item.title === "Mechanical"
                  )?._id
                : formData?.category?.value,
          };
          if (subscription) {
            modifiedData.content = subscriptionContent;
          }
          if (!promo) {
            delete modifiedData?.promoCode;
            delete modifiedData?.promoCodeOptions;
          }

          delete modifiedData?.subscription_duration;
          delete modifiedData?.subscription_price;
          modifiedData.image = formatImageValueForSubmit(formData.image);

          staticPageCreateTrigger(modifiedData);
        } else {
          showErrorToast(error);
        }
      },
    }
  );

  const { trigger: staticPageCreateTrigger, isMutating: createMutating } =
    useSWRMutation(StaticPageApiUrls.create, sendPostRequestAuth, {
      onSuccess: () => {
        toast.success("Success");
      },
      onError: (error) => {
        showErrorToast(error);
      },
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const subscriptionContent = subscription
      ? JSON.stringify({
          subscription_price: formData.subscription_price,
          subscription_duration: formData.subscription_duration,
        })
      : undefined;
    const modifiedData = {
      ...formData,
      category:
        category === "civil"
          ? categoryData?.data?.find((item) => item.title === "Civil")?._id
          : category === "mechanical"
          ? categoryData?.data?.find((item) => item.title === "Mechanical")?._id
          : formData?.category?.value,
      [type]: formData?.[type].map((item) => item.value),
      promoCode: formData?.promoCode
        ? JSON.stringify(formData?.promoCode)
        : null,
      promoCodeOptions: JSON.stringify(promoCodeOptions),
    };
    if (subscription) {
      modifiedData.content = subscriptionContent;
    }

    if (!promo) {
      delete modifiedData?.promoCode;
      delete modifiedData?.promoCodeOptions;
    }

    modifiedData.image = formatImageValueForSubmit(formData.image);

    delete modifiedData?.subscription_duration;
    delete modifiedData?.subscription_price;

    delete modifiedData.slug;
    trigger(modifiedData);
  };

  return (
    <div className="mt-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <Input
          label="Page title"
          name="title"
          required
          disabled={title !== ""}
          placeholder="Enter page title"
          onChange={handleInputChange}
          error={errors.title}
          value={formData.title}
        />
        <Input
          label="Display title"
          name="displayTitle"
          placeholder="Enter display title"
          onChange={handleInputChange}
          error={errors.displayTitle}
          value={formData.displayTitle}
        />
        <Input
          label="Page slug"
          name="slug"
          required
          placeholder="Enter page slug"
          onChange={handleInputChange}
          error={errors.slug}
          value={formData.slug}
          disabled
        />
        <Selector
          name="category"
          label="Category"
          value={formData?.category}
          options={categoryData?.data?.map((item) => ({
            label: item.title,
            value: item._id,
          }))}
          isClearable={false}
          onChange={handleInputChange}
          error={errors?.category}
          required
          disabled={typeof category !== "undefined"}
        />
        <ToggleSwitch
          label="Visibility"
          name="visibility"
          value={formData.visibility}
          onChange={handleInputChange}
          required
          error={errors?.visibility}
        />
        <Input
          label="Header Description"
          name="headerDescription"
          required
          placeholder="Enter header description"
          onChange={handleInputChange}
          error={errors.headerDescription}
          value={formData.headerDescription}
        />
        <HtmlEditor
          label="Body Description"
          name="bodyDescription"
          placeholder="Enter body description"
          onChange={handleInputChange}
          error={errors.bodyDescription}
          value={formData.bodyDescription}
        />
        {/* <ImageInput
          label="Upload Image"
          name="image"
          onChange={handleInputChange}
          error={errors.image}
          value={formData.image}
        /> */}
        <ImageSelector
          label="Upload Image"
          name="image"
          setModalOpen={setModalOpen}
          onChange={handleInputChange}
          value={formData.image}
          multiple={multipleImage}

        />
        {typeData && (
          <Selector
            name={type}
            label={type}
            value={formData?.[type]}
            options={typeData?.data
              ?.filter((i) => {
                if (!category) {
                  return true;
                }
                return (
                  (i?.category?.title === "Civil" && category === "civil") ||
                  (i?.category?.title === "Mechanical" &&
                    category === "mechanical")
                );
              })
              ?.filter((i) => {
                if (!filter) {
                  return true;
                }
                return i?.[filter] === true;
              })
              .map((item) => ({
                label: item.title,
                value: item._id,
              }))}
            isClearable={false}
            onChange={handleInputChange}
            error={errors?.[type]}
            multiple
            isSearchable
            // isLoading={isLoading}
          />
        )}
        {subscription && (
          <>
            <Input
              label="Subscription Price"
              name="subscription_price"
              required
              placeholder="Enter subscription price"
              onChange={handleInputChange}
              error={errors.subscription_price}
              value={formData.subscription_price}
              type="number"
            />
            <Input
              label="Subscription Duration"
              name="subscription_duration"
              required
              placeholder="Enter subscription duration"
              onChange={handleInputChange}
              error={errors.subscription_duration}
              value={formData.subscription_duration}
              type="number"
            />
          </>
        )}
        {promo && (
          <Selector
            name="promoCode"
            label="Promo Codes- Number at last indicates discount percantage (example- abc-30)"
            value={formData?.promoCode}
            options={promoCodeOptions}
            onChange={handleInputChange}
            placeholder="Select or create a promo code"
            isSearchable
            isClearable
            multiple
            creatable
            formatCreateLabel={(inputValue) =>
              `Create promo code "${inputValue}"`
            }
            error={errors.promoCode}
          />
        )}
        <div className="flex justify-end gap-4">
          <Button
            onClick={handleSubmit}
            type="primary"
            size="small"
            isLoading={isMutating || createMutating}
          >
            Confirm
          </Button>
        </div>
      </form>
      <Modal
        isOpen={isModalOpen}
        title={"Storage Manager"}
        // onConfirm={handleSubmit}
        onCancel={() => {
          setModalOpen(false);
        }}
        footer={false}
        
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length) {
              const selectedFiles = file
                .map((item) => item?.fileUrl || item)
                .filter(Boolean);
              if (isMultipleImagesEnabled) {
                handleInputChange("image", selectedFiles);
              } else {
                handleInputChange("image", selectedFiles[0] || "");
              }
            }
            setModalOpen(false);
          }}
          multiple={multipleImage}
        />
      </Modal>
    </div>
  );
};

export default StaticPageForm;
